/**
 * ถ้ำคลื่นคำราม (docs/systems/DUNGEON_Y1.md): the first dungeon, run on its interior floor (data/dungeon.ts, interiors.ts).
 *
 * - Entering by the cave mouth on the island (an interior door) starts a run: the four rooms' people scaled by the
 *   party (1 + party members), the kidnapped villagers, the school helpers' herald. Going out ends it; going in again
 *   is a fresh run (the reset). One copy per party, as an interior floor is (the server's instance model later).
 * - Rooms are pulled as a pack; the passage out of a room is barred until everyone in it is down.
 * - The people's special moves are `scripted` skills: the World winds them up, this system resolves their shapes
 *   (cones, rings, lines, the whirlpool's pull), the harpoon's pull, the chain's root, sleep powder, the jailer's
 *   reflecting spin, the mini-boss's three dashes and decoys, the boss's enrage and adds; room 3's surf lanes beat.
 * - The player falling is a wipe: the bosses heal and calm down, their adds and decoys go; cleared rooms stay.
 * - The boss's chest pays once per arena day per character (`progress.dungeon`); its extras grow with each villager
 *   freed. Normal people drop loot every run (data/drops.ts).
 *
 * Randomness only through the host's `random`, time only from `dt`.
 */
import { stacksOf } from '../status';
import type { ActivityMode } from '../data/modes';
import { modeNow } from './modes';
import { getArt, getSkill, SKILLS } from '../data/skills';
import { ITEMS } from '../data/items';
import { SCHOOLS } from '../data/schools';
import { PARTY } from '../data/party';
import {
  BOSS_CHEST,
  DUNGEON,
  DUNGEON_HELPER_NPC,
  DUNGEON_ID,
  DUNGEON_MOVE_RULES,
  DUNGEON_NAME,
  DUNGEON_PLAN,
  DUNGEON_ROOMS,
  DUNGEON_SET,
  DUNGEON_SHORTCUT,
  DUNGEON_VILLAGERS,
  HELPER_NAMES,
  SURF_LANES,
  WANDERER_HELPERS,
  dungeonDmgScale,
  dungeonHpScale,
  newDungeonSave,
  partySize,
  type PlanPt,
  type PlanRect,
} from '../data/dungeon';
import { BAG_SIZE } from '../data/items';
import { dist, MOVEMENT, type Entity, type WorldEvent } from '../world';
import type { SkillKind } from '../data/skills';
import { inShape, shapeOf, type BossShape } from './bossMoves';
import type { SystemHost, WorldSystem } from './types';

/** A ground telegraph's shape (systems/bossMoves.ts). */
export type DungeonShape = BossShape;

export type DungeonEvent =
  /** A run began: the party size it is scaled for. */
  | { type: 'dungeonStart'; party: number }
  | { type: 'dungeonRoom'; room: number; state: 'engaged' | 'cleared' }
  /** A move's ground telegraph, shown for `seconds`; it ends (landed or cancelled) with `dungeonTeleEnd`. */
  | { type: 'dungeonTele'; id: number; srcId: number; move: string; shape: DungeonShape; seconds: number }
  | { type: 'dungeonTeleEnd'; id: number; cancelled: boolean }
  | { type: 'dungeonPhase'; id: number; phase: 'decoys' | 'enrage' | 'calm' | 'spin' }
  /** The mini-boss's decoys: the real one leaves wet footprints. */
  | { type: 'dungeonDecoys'; realId: number; decoyIds: number[] }
  | { type: 'dungeonReflect'; srcId: number; tgtId: number }
  /** A channel broken by a break (破). */
  | { type: 'dungeonCancel'; id: number; move: string }
  | { type: 'dungeonRescue'; freed: number; total: number }
  | { type: 'dungeonWipe' }
  | { type: 'dungeonHelpers'; ids: number[] }
  | { type: 'dungeonHeal'; srcId: number; tgtId: number; amount: number }
  | { type: 'dungeonClear'; rewarded: boolean; items: string[]; gold: number; cultivation: number };

interface Tele {
  readonly id: number;
  readonly move: string;
  readonly shape: DungeonShape;
}

interface RoomRun {
  readonly n: number;
  readonly ids: number[];
  engaged: boolean;
  cleared: boolean;
}

interface Run {
  readonly party: number;
  /** `npc`: the party is you and the school helpers; `pvp`: other players (stub, falls back to `npc`). */
  readonly mode: ActivityMode;
  readonly ox: number;
  readonly oy: number;
  readonly rooms: RoomRun[];
  readonly villagers: number[];
  freed: number;
  readonly leaving: Map<number, number>;
  readonly herald: number;
  helpers: number[];
  readonly teles: Map<number, Tele>;
  readonly dashes: { id: number; dirs: [number, number][]; t: number }[];
  readonly shadows: { x: number; y: number; t: number }[];
  readonly spins: Map<number, number>;
  readonly heals: Map<number, number>;
  decoys: { ids: number[]; t: number } | null;
  decoysDone: boolean;
  enraged: boolean;
  adds: number[];
  surfT: number;
  surf: { lane: PlanRect; t: number; id: number } | null;
  bossDown: boolean;
  last: { x: number; y: number };
  noticeT: number;
}

const SYSTEMS = new WeakMap<object, DungeonSystem>();
const SLEEP_RESIST = 'dg-calm-pearl';
const BOSS = 'dg-sha-tian';
const MINI = 'dg-silver-wave';

export class DungeonSystem implements WorldSystem {
  readonly name = 'dungeon';
  run: Run | null = null;
  private teleIds = 1;

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  private get w() {
    return this.h.world;
  }

  // ───────────────────────── plan ↔ map ─────────────────────────

  private pt(p: PlanPt): { x: number; y: number } {
    const r = this.run!;
    return { x: p[0] + r.ox + 0.5, y: p[1] + r.oy + 0.5 };
  }

  private rect(q: PlanRect): { x0: number; x1: number; y0: number; y1: number } {
    const r = this.run!;
    return { x0: q[0] + r.ox, x1: q[1] + r.ox + 1, y0: q[2] + r.oy, y1: q[3] + r.oy + 1 };
  }

  private inRect(q: PlanRect, e: { x: number; y: number }): boolean {
    const b = this.rect(q);
    return e.x >= b.x0 && e.x < b.x1 && e.y >= b.y0 && e.y < b.y1;
  }

  // ───────────────────────── a run ─────────────────────────

  private start(): void {
    const w = this.w;
    const m = w.map;
    const pw = DUNGEON_PLAN[0]!.length;
    const ph = DUNGEON_PLAN.length;
    const party = partySize(1 + w.partyMembers().filter((e) => !e.dead).length);
    this.run = {
      party,
      mode: modeNow(w, 'dungeon'),
      ox: Math.floor((m.w - pw) / 2),
      oy: Math.floor((m.h - ph) / 2),
      rooms: [],
      villagers: [],
      freed: 0,
      leaving: new Map(),
      herald: -1,
      helpers: [],
      teles: new Map(),
      dashes: [],
      shadows: [],
      spins: new Map(),
      heals: new Map(),
      decoys: null,
      decoysDone: false,
      enraged: false,
      adds: [],
      surfT: DUNGEON.surfBeat,
      surf: null,
      bossDown: false,
      last: { x: w.player.x, y: w.player.y },
      noticeT: 0,
    };
    const run = this.run;
    for (const room of DUNGEON_ROOMS) {
      const ids: number[] = [];
      for (const s of room.spawns)
        if ((s.minParty ?? 1) <= party) ids.push(this.spawn(s.monster, this.pt(s.at)).id);
      run.rooms.push({ n: room.n, ids, engaged: false, cleared: false });
    }
    for (const v of DUNGEON_VILLAGERS) {
      const at = this.pt(v.at);
      run.villagers.push(
        this.h.add({ kind: 'npc', ...at, sprite: v.sprite, name: v.name, title: 'ถูกจับมาขาย' }).id,
      );
    }
    const hn = DUNGEON_HELPER_NPC;
    const herald = this.h.add({
      kind: 'npc',
      ...this.pt(hn.at),
      sprite: hn.sprite,
      name: hn.name,
      title: hn.title,
    });
    (run as { herald: number }).herald = herald.id;
    this.h.emit({ type: 'dungeonStart', party });
    this.h.log('sys', `เข้าสู่${DUNGEON_NAME} (ปาร์ตี้ ${party} คน)`);
  }

  private end(): void {
    const run = this.run;
    if (!run) return;
    for (const id of run.helpers) this.w.entities.delete(id);
    this.run = null;
  }

  /** A dungeon person at a spot, scaled to the party. */
  private spawn(id: string, at: { x: number; y: number }): Entity {
    const e = this.h.spawnMonster(id, at.x, at.y);
    const n = this.run!.party;
    const hp = Math.round(e.maxHp * dungeonHpScale(n));
    Object.assign(e, { hp, maxHp: hp, atk: e.atk * dungeonDmgScale(n), magic: e.magic * dungeonDmgScale(n) });
    return e;
  }

  /** Everyone a source may strike (the player, the party, the helpers), alive. */
  private victims(src: Entity): Entity[] {
    return [...this.w.entities.values()].filter((o) => !o.dead && o !== src && this.w.isHostile(src, o));
  }

  private airborne(e: Entity): boolean {
    return e.z > MOVEMENT.airDodgeHeight;
  }

  /** A blow escaped in the air: shown as a dodge. */
  private dodged(src: Entity, v: Entity, skillId: string): void {
    this.h.emit({
      type: 'hit',
      srcId: src.id,
      tgtId: v.id,
      skillId,
      outcome: 'dodge',
      damage: 0,
      crit: false,
    });
  }

  private push(e: Entity, fromX: number, fromY: number, d: number): void {
    let dx = e.x - fromX;
    let dy = e.y - fromY;
    const l = Math.hypot(dx, dy) || 1;
    [dx, dy] = [dx / l, dy / l];
    for (let k = 0; k < d / 0.25; k++) if (!this.h.move(e, dx * 0.25, dy * 0.25)) break;
  }

  private notice(text: string): void {
    const run = this.run;
    if (run && run.noticeT > 0) return;
    if (run) run.noticeT = 2;
    this.h.notice(text);
  }

  // ───────────────────────── hooks ─────────────────────────

  observe(e: WorldEvent): void {
    if (e.type === 'enterInterior' && e.interior === DUNGEON_ID) return this.start();
    if (e.type === 'exitInterior' && e.interior === DUNGEON_ID) return this.end();
    const run = this.run;
    if (!run) return;
    switch (e.type) {
      case 'castStart':
        return this.castStart(e.srcId, e.skillId);
      case 'skill':
        return this.resolve(e.srcId, e.skillId, e.tgtId);
      case 'hit':
        return this.onHit(e.srcId, e.tgtId, e.skillId, e.outcome, e.damage);
      case 'death':
        return this.onDeath(e.id);
    }
  }

  monsterDecide(m: Entity, foe: Entity): boolean {
    const run = this.run;
    const id = m.brain?.defId ?? '';
    if (!run || !id.startsWith('dg-')) return false;
    const w = this.w;
    const r = () => this.h.random();
    const d = dist(m, foe);
    const ready = (s: string) => !m.cooldowns[s];
    switch (id) {
      case 'dg-harpooner':
        if (d < 2.6 && ready('dg-backstep')) {
          this.backstep(m, foe);
          return true;
        }
        return false;
      case 'dg-doctor': {
        let ally: Entity | null = null;
        for (const o of w.entities.values())
          if (
            o.kind === 'monster' &&
            !o.dead &&
            o.brain?.defId.startsWith('dg-') &&
            dist(o, m) <= 7 &&
            o.hp < o.maxHp * 0.55
          )
            if (!ally || o.hp / o.maxHp < ally.hp / ally.maxHp) ally = o;
        if (ally && ready('dg-salt-salve')) {
          run.heals.set(m.id, ally.id);
          if (w.useSkill(m, 'dg-salt-salve', foe)) return true;
        }
        if (ready('dg-sleep-powder') && d <= 4.2 && r() < 0.5) return w.useSkill(m, 'dg-sleep-powder', foe);
        return false;
      }
      case 'dg-jailer': {
        const ranged = !!foe.cast && getSkill(foe.cast.skillId).range >= 4;
        if (ready('dg-chain-spin') && (ranged || r() < 0.25)) return w.useSkill(m, 'dg-chain-spin', foe);
        return false;
      }
      case MINI:
        if (!run.decoysDone && m.hp < m.maxHp * DUNGEON.decoysAt) {
          run.decoysDone = true;
          if (w.useSkill(m, 'dg-moon-decoys', foe)) return true;
        }
        if (ready('dg-triple-wave') && r() < 0.45) return w.useSkill(m, 'dg-triple-wave', foe);
        if (ready('dg-frost-cut') && d <= 2.6 && r() < 0.4) return w.useSkill(m, 'dg-frost-cut', foe);
        return false;
      case BOSS: {
        if (!run.enraged && m.hp < m.maxHp * DUNGEON.enrageAt) {
          run.enraged = true;
          if (w.useSkill(m, 'dg-iron-body', foe)) return true;
        }
        const big = [
          ...(ready('dg-giant-palm') && d <= 4.6 ? ['dg-giant-palm'] : []),
          ...(ready('dg-sea-turn') && d <= 3.4 ? ['dg-sea-turn'] : []),
          ...(ready('dg-sea-split') && d <= 10 ? ['dg-sea-split'] : []),
          ...(ready('dg-whirlpool') ? ['dg-whirlpool'] : []),
        ];
        if (big.length && r() < 0.6) return w.useSkill(m, big[Math.floor(r() * big.length)]!, foe);
        return false;
      }
    }
    return false;
  }

  talk(npc: Entity): boolean {
    const run = this.run;
    if (!run) return false;
    const say = (text: string) => this.h.emit({ type: 'dialog', npcId: npc.id, text, action: null });
    if (npc.id === run.herald) {
      if (run.helpers.length) {
        for (const id of run.helpers) this.w.entities.delete(id);
        run.helpers = [];
        this.h.emit({ type: 'dungeonHelpers', ids: [] });
        say('ศิษย์ทั้งสองกลับไปรายงานสำนักแล้ว ขอให้โชคดี');
      } else if (run.party > 1) say('เจ้ามีเพื่อนร่วมทางแล้ว ศิษย์ของสำนักขอเฝ้าปากถ้ำไว้ให้');
      else {
        this.helpers();
        say('สำนักส่งศิษย์สองคนมาช่วย พูดกับข้าอีกครั้งหากอยากไปลำพัง');
      }
      return true;
    }
    const k = run.villagers.indexOf(npc.id);
    if (k < 0) return false;
    if (run.leaving.has(npc.id)) return true;
    const guard = run.rooms[1]!.ids.some((id) => {
      const g = this.w.entities.get(id);
      return g && !g.dead && dist(g, npc) <= 9;
    });
    if (guard) {
      say('ผู้คุมยังอยู่! ปราบพวกมันก่อนแล้วค่อยแก้เชือกให้ข้า');
      return true;
    }
    run.freed++;
    run.leaving.set(npc.id, 1.5);
    say('ขอบคุณ ท่านจอมยุทธ์! ข้าจะหนีออกทางท่าเรือ ระวังห้องข้างในด้วย');
    this.h.emit({ type: 'dungeonRescue', freed: run.freed, total: DUNGEON_VILLAGERS.length });
    this.h.log('sys', `ช่วยชาวบ้านได้ ${run.freed}/${DUNGEON_VILLAGERS.length} คน หีบสมบัติจะมีของมากขึ้น`);
    return true;
  }

  step(dt: number): void {
    const run = this.run;
    if (!run) return;
    const w = this.w;
    if (w.map.interior?.id !== DUNGEON_ID) return this.end();
    run.noticeT = Math.max(0, run.noticeT - dt);
    // Telegraphs whose cast is gone (stunned, dead) end cancelled.
    for (const [src, t] of run.teles) {
      const e = w.entities.get(src);
      if (!e || e.dead || e.cast?.skillId !== t.move) {
        run.teles.delete(src);
        this.h.emit({ type: 'dungeonTeleEnd', id: t.id, cancelled: true });
      }
    }
    // The whirlpool's pull, while it channels: everyone on the ground is dragged in.
    for (const e of w.entities.values())
      if (!e.dead && e.cast?.skillId === 'dg-whirlpool')
        for (const v of this.victims(e)) {
          const d = dist(v, e);
          if (this.airborne(v) || d > (DUNGEON_MOVE_RULES['dg-whirlpool']!.r ?? 10) || d < 1.2) continue;
          const s = Math.min(d - 1.2, 2.4 * dt);
          this.h.move(v, ((e.x - v.x) / d) * s, ((e.y - v.y) / d) * s);
        }
    this.stepDashes(dt);
    for (let i = run.shadows.length - 1; i >= 0; i--) {
      const s = run.shadows[i]!;
      s.t -= dt;
      if (s.t <= 0) run.shadows.splice(i, 1);
      else
        for (const v of [w.player, ...w.partyMembers()])
          if (
            !v.dead &&
            !this.airborne(v) &&
            Math.hypot(v.x - s.x, v.y - s.y) < 1 &&
            !stacksOf(v.statuses, 'slow')
          )
            w.addStatus(v, 'slow', 1);
    }
    for (const [id, t] of run.spins) {
      const e = w.entities.get(id);
      if (t - dt <= 0 || !e || e.dead) {
        run.spins.delete(id);
        if (e) this.h.emit({ type: 'dungeonPhase', id, phase: 'calm' });
      } else run.spins.set(id, t - dt);
    }
    if (run.decoys && (run.decoys.t -= dt) <= 0) this.clearDecoys();
    for (const [id, t] of run.leaving)
      if (t - dt <= 0) {
        run.leaving.delete(id);
        w.entities.delete(id);
      } else run.leaving.set(id, t - dt);
    this.stepRooms();
    this.stepSurf(dt);
    this.stepGates();
  }

  // ───────────────────────── rooms, gates, the surf ─────────────────────────

  private stepRooms(): void {
    const run = this.run!;
    for (const room of run.rooms) {
      if (room.cleared) continue;
      const people = room.ids.map((id) => this.w.entities.get(id)).filter((e): e is Entity => !!e && !e.dead);
      if (!people.length) {
        room.cleared = true;
        this.h.emit({ type: 'dungeonRoom', room: room.n, state: 'cleared' });
        if (room.n < 4) this.h.notice(`${DUNGEON_ROOMS[room.n - 1]!.name}ราบคาบ ประตูซี่กรงเปิดออก`);
        continue;
      }
      const fighting = people.some((e) => e.brain?.state === 'chase');
      if (fighting && !room.engaged) {
        room.engaged = true;
        this.h.emit({ type: 'dungeonRoom', room: room.n, state: 'engaged' });
      }
      if (!fighting) room.engaged = false;
      // A room fights as one: the rest come when one is pulled.
      if (fighting)
        for (const e of people)
          if (e.brain && e.brain.state === 'idle') {
            e.brain.state = 'chase';
            e.brain.decisionT = 0.5;
          }
    }
  }

  private stepGates(): void {
    const run = this.run!;
    const p = this.w.player;
    if (p.dead) return;
    for (let k = 0; k < DUNGEON_ROOMS.length; k++) {
      const gate = DUNGEON_ROOMS[k]!.gate;
      if (!gate || run.rooms[k]!.cleared || !this.inRect(gate, p)) continue;
      Object.assign(p, { x: run.last.x, y: run.last.y, path: null });
      if (this.inRect(gate, p)) Object.assign(p, this.pt([gate[0], gate[3] + 1]));
      this.notice('ประตูซี่กรงปิดอยู่ ปราบโจรในห้องนี้ให้หมดก่อน');
    }
    if (run.bossDown && dist(p, this.pt(DUNGEON_SHORTCUT)) < 0.8) {
      const at = this.w.map.spawn;
      for (const e of [p, ...this.w.partyMembers(), ...run.helpers.map((id) => this.w.entities.get(id)!)])
        if (e) Object.assign(e, { x: at.x, y: at.y, path: null });
      this.notice('ทางน้ำลับพากลับมาถึงท่าเรือลับ');
    }
    run.last = { x: p.x, y: p.y };
  }

  private stepSurf(dt: number): void {
    const run = this.run!;
    const room = run.rooms[2]!;
    const mini = room.ids
      .map((id) => this.w.entities.get(id))
      .find((e) => e?.brain?.defId === MINI && !e.dead);
    if (run.surf) {
      run.surf.t -= dt;
      if (run.surf.t > 0) return;
      const { lane, id } = run.surf;
      run.surf = null;
      this.h.emit({ type: 'dungeonTeleEnd', id, cancelled: !mini });
      if (!mini) return;
      const b = this.rect(lane);
      for (const v of this.victims(mini)) {
        if (!this.inRect(lane, v)) continue;
        if (this.airborne(v)) {
          this.dodged(mini, v, 'dg-surf-lane');
          continue;
        }
        this.h.hit(mini, v, getSkill('dg-surf-lane').power, 'power', 'dg-surf-lane');
        this.push(v, b.x0 - 4, v.y, 2);
      }
      return;
    }
    if (!mini || !room.engaged) return;
    run.surfT -= dt;
    if (run.surfT > 0) return;
    run.surfT = DUNGEON.surfBeat;
    const lane = SURF_LANES[Math.floor(this.h.random() * SURF_LANES.length)]!;
    const id = this.teleIds++;
    run.surf = { lane, t: DUNGEON.surfWarn, id };
    const b = this.rect(lane);
    this.h.emit({
      type: 'dungeonTele',
      id,
      srcId: mini.id,
      move: 'dg-surf-lane',
      shape: { k: 'rect', ...b },
      seconds: DUNGEON.surfWarn,
    });
  }

  // ───────────────────────── moves ─────────────────────────

  /** The shape a scripted move will land on, fixed when its wind-up begins. */
  private shapeOf(src: Entity, move: string, tgt: Entity | undefined): DungeonShape | null {
    const rule = DUNGEON_MOVE_RULES[move];
    return rule ? shapeOf(rule, src, tgt) : null;
  }

  private castStart(srcId: number, move: string): void {
    const run = this.run!;
    const src = this.w.entities.get(srcId);
    if (!src || !DUNGEON_MOVE_RULES[move]) return;
    const shape = this.shapeOf(src, move, this.w.entities.get(src.cast?.targetId ?? -1));
    if (!shape) return;
    const t: Tele = { id: this.teleIds++, move, shape };
    run.teles.set(srcId, t);
    this.h.emit({ type: 'dungeonTele', id: t.id, srcId, move, shape, seconds: getSkill(move).windup });
  }

  /** Whether a point stands in a shape. */
  static inShape(s: DungeonShape, p: { x: number; y: number }): boolean {
    return inShape(s, p);
  }

  private resolve(srcId: number, move: string, tgtId: number | null): void {
    const run = this.run!;
    const rule = DUNGEON_MOVE_RULES[move];
    const src = this.w.entities.get(srcId);
    if (!rule?.scripted || !src || src.dead) return;
    const s = getSkill(move);
    const w = this.w;
    switch (rule.scripted) {
      case 'heal': {
        const ally = w.entities.get(run.heals.get(srcId) ?? -1);
        run.heals.delete(srcId);
        if (!ally || ally.dead) return;
        const amount = Math.round(ally.maxHp * (s.healPct ?? 0.25));
        ally.hp = Math.min(ally.maxHp, ally.hp + amount);
        this.h.emit({ type: 'dungeonHeal', srcId, tgtId: ally.id, amount });
        return;
      }
      case 'reflect':
        src.blocking = true;
        if (src.brain) src.brain.blockT = 2.5;
        run.spins.set(srcId, 2.5);
        this.h.emit({ type: 'stance', id: srcId });
        this.h.emit({ type: 'dungeonPhase', id: srcId, phase: 'spin' });
        return;
      case 'dash3': {
        const tgt = w.entities.get(tgtId ?? -1);
        const a = tgt ? Math.atan2(tgt.y - src.y, tgt.x - src.x) : Math.atan2(src.dirY, src.dirX);
        const dirs: [number, number][] = [0, (2 * Math.PI) / 3, (-2 * Math.PI) / 3].map((da, k) => [
          Math.cos(a + (k ? Math.PI : 0) + da),
          Math.sin(a + (k ? Math.PI : 0) + da),
        ]);
        run.dashes.push({ id: srcId, dirs, t: 0 });
        return;
      }
      case 'decoys':
        return this.decoys(src);
      case 'enrage': {
        w.addStatus(src, 'dg-iron-skin', 1);
        for (const k of [-1, 1]) {
          const add = this.spawn('dg-grunt', { x: src.x + k * 1.6, y: src.y + 1.2 });
          add.brain!.state = 'chase';
          run.adds.push(add.id);
        }
        this.h.emit({ type: 'dungeonPhase', id: srcId, phase: 'enrage' });
        this.h.notice('ซาเทียนคำราม! ร่างเหล็กใต้สมุทร — สมุนสองคนกระโดดเข้ามา');
        return;
      }
    }
    // Shapes: cone, ring, line, the whirlpool's burst.
    const t = run.teles.get(srcId);
    run.teles.delete(srcId);
    const shape =
      rule.scripted === 'pull'
        ? ({ k: 'ring', x: src.x, y: src.y, r: 3 } as const)
        : ((t?.move === move ? t.shape : null) ?? this.shapeOf(src, move, w.entities.get(tgtId ?? -1)));
    if (t) this.h.emit({ type: 'dungeonTeleEnd', id: t.id, cancelled: false });
    if (!shape) return;
    const kind: SkillKind = s.kind === 'heal' || s.kind === 'buff' ? 'power' : s.kind;
    for (const v of this.victims(src)) {
      if (!DungeonSystem.inShape(shape, v)) continue;
      if (this.airborne(v)) {
        this.dodged(src, v, move);
        continue;
      }
      const wasBlocking = v.blocking;
      const out = this.h.hit(src, v, s.power, kind, move, s.stun);
      const landed = out === 'hit' || out === 'guardBreak' || out === 'interrupt';
      if (!landed || v.dead) continue;
      switch (move) {
        case 'dg-giant-palm':
          if (!wasBlocking) {
            v.stunT = Math.max(v.stunT, 100 / (100 + v.toughness));
            v.cast = null;
          }
          this.push(v, src.x, src.y, rule.knockback ?? 2.5);
          break;
        case 'dg-harpoon-hook': {
          // Pulled in to the harpooner's feet.
          const d = dist(v, src);
          if (d > 1.3) {
            const k = (d - 1.2) / d;
            for (let i = 0; i < 8; i++) this.h.move(v, ((src.x - v.x) * k) / 8, ((src.y - v.y) * k) / 8);
          }
          break;
        }
        case 'dg-dragon-chain':
          w.addStatus(v, 'dg-root', 1, srcId);
          break;
        case 'dg-sleep-powder':
          if (out !== 'hit') break;
          if (this.wearsPearl(v)) {
            if (v === w.player) this.notice('ไข่มุกสงบคลื่นเย็นวาบ ผงหลับใหลไม่ได้ผล');
            break;
          }
          v.stunT = Math.max(v.stunT, DUNGEON.sleepSeconds);
          v.blocking = false;
          v.cast = null;
          w.addStatus(v, 'dg-sleep', 1, srcId);
          break;
      }
    }
  }

  private wearsPearl(v: Entity): boolean {
    return v === this.w.player && this.w.progress.equipment.treasure === SLEEP_RESIST;
  }

  private onHit(srcId: number, tgtId: number, skillId: string, outcome: string, damage: number): void {
    const run = this.run!;
    const w = this.w;
    const tgt = w.entities.get(tgtId);
    const src = w.entities.get(srcId);
    if (!tgt || !src) return;
    // Struck awake.
    if (damage > 0 && stacksOf(tgt.statuses, 'dg-sleep') && skillId !== 'dg-sleep-powder') {
      w.addStatus(tgt, 'dg-sleep', -1);
      tgt.stunT = 0;
    }
    // The jailer's spin turns ranged blows back.
    const sk = SKILLS[skillId];
    if (
      run.spins.has(tgtId) &&
      sk &&
      (sk.range >= 4 || sk.volley) &&
      outcome !== 'dodge' &&
      skillId !== 'dg-chain-spin'
    ) {
      tgt.hp = Math.min(tgt.maxHp, tgt.hp + damage);
      this.h.emit({ type: 'dungeonReflect', srcId: tgtId, tgtId: srcId });
      this.h.hit(tgt, src, getSkill('dg-chain-spin').power, 'attack', 'dg-chain-spin');
    }
    // A break landing on the whirlpool's channel breaks it.
    if (
      tgt.cast?.skillId === 'dg-whirlpool' &&
      sk?.kind === 'break' &&
      (outcome === 'hit' || outcome === 'guardBreak')
    ) {
      tgt.cast = null;
      tgt.stunT = Math.max(tgt.stunT, 1.5);
      const t = run.teles.get(tgtId);
      run.teles.delete(tgtId);
      if (t) this.h.emit({ type: 'dungeonTeleEnd', id: t.id, cancelled: true });
      this.h.emit({ type: 'dungeonCancel', id: tgtId, move: 'dg-whirlpool' });
      this.h.log('fight', 'ท่าทำลายตัดวังน้ำวนของซาเทียนขาดกลางคัน!');
    }
  }

  private onDeath(id: number): void {
    const w = this.w;
    if (id === w.player.id) return this.wipe();
    const e = w.entities.get(id);
    const def = e?.brain?.defId;
    if (def === MINI) this.clearDecoys();
    if (def === BOSS) this.chest();
  }

  // ───────────────────────── the mini-boss ─────────────────────────

  private backstep(m: Entity, foe: Entity): void {
    m.cooldowns['dg-backstep'] = getSkill('dg-backstep').cooldown;
    const [fx, fy] = [m.x, m.y];
    this.push(m, foe.x, foe.y, 2.5);
    this.h.emit({ type: 'skillDash', id: m.id, fromX: fx, fromY: fy, skillId: 'dg-backstep' });
  }

  private stepDashes(dt: number): void {
    const run = this.run!;
    for (let i = run.dashes.length - 1; i >= 0; i--) {
      const q = run.dashes[i]!;
      const m = this.w.entities.get(q.id);
      if (!m || m.dead || m.stunT > 0) {
        run.dashes.splice(i, 1);
        continue;
      }
      q.t -= dt;
      if (q.t > 0) continue;
      q.t = 0.25;
      const [dx, dy] = q.dirs.shift()!;
      const rule = DUNGEON_MOVE_RULES['dg-triple-wave']!;
      const [fx, fy] = [m.x, m.y];
      const struck = new Set<number>();
      for (let k = 0; k < (rule.len ?? 5) / 0.2; k++) {
        if (!this.h.move(m, dx * 0.2, dy * 0.2)) break;
        for (const v of this.victims(m)) if (dist(v, m) <= (rule.half ?? 0.9)) struck.add(v.id);
      }
      this.h.emit({ type: 'skillDash', id: m.id, fromX: fx, fromY: fy, skillId: 'dg-triple-wave' });
      for (const id of struck) {
        const v = this.w.entities.get(id);
        if (!v || v.dead) continue;
        if (this.airborne(v)) this.dodged(m, v, 'dg-triple-wave');
        else this.h.hit(m, v, getSkill('dg-triple-wave').power, 'attack', 'dg-triple-wave');
      }
      run.shadows.push({ x: m.x, y: m.y, t: 4 });
      if (!q.dirs.length) run.dashes.splice(i, 1);
    }
  }

  private decoys(m: Entity): void {
    const run = this.run!;
    const ids: number[] = [];
    const spots = [
      { x: m.x, y: m.y },
      { x: m.x + 2, y: m.y + 1 },
      { x: m.x - 2, y: m.y + 1 },
    ];
    // Shuffle who stands where: the real one is anywhere of the three.
    const real = Math.floor(this.h.random() * 3);
    const [rx, ry] = [spots[real]!.x, spots[real]!.y];
    spots.splice(real, 1);
    for (const at of spots) {
      const d = this.h.spawnMonster('dg-moon-shadow', at.x, at.y);
      d.brain!.state = 'chase';
      d.facing = m.facing;
      ids.push(d.id);
    }
    if (real !== 0) {
      const fx = m.x;
      const fy = m.y;
      this.h.move(m, rx - fx, 0);
      this.h.move(m, 0, ry - fy);
    }
    run.decoys = { ids, t: DUNGEON.decoySeconds };
    this.h.emit({ type: 'dungeonDecoys', realId: m.id, decoyIds: ids });
    this.h.emit({ type: 'dungeonPhase', id: m.id, phase: 'decoys' });
  }

  private clearDecoys(): void {
    const run = this.run!;
    if (!run.decoys) return;
    for (const id of run.decoys.ids) this.w.entities.delete(id);
    run.decoys = null;
  }

  // ───────────────────────── helpers, wipe, chest ─────────────────────────

  private helpers(): void {
    const run = this.run!;
    const w = this.w;
    const p = w.player;
    const sc = w.progress.school ? SCHOOLS[w.progress.school] : undefined;
    const builds = sc
      ? HELPER_NAMES.map((n, k) => ({
          name: `${n}${sc.name}`,
          sprite: k ? `${sc.uniform}F` : sc.uniform,
          art: sc.arts[k] ?? sc.arts[0]!,
          title: sc.discipleTitle,
        }))
      : WANDERER_HELPERS.map((h) => ({ ...h, title: 'จอมยุทธ์พเนจร' }));
    const k = PARTY.strength;
    for (const [i, b] of builds.entries()) {
      const at = { x: p.x + (i ? 1 : -1), y: p.y - 0.8 };
      const e = this.h.add({
        kind: 'rival',
        ...at,
        name: b.name,
        title: b.title,
        sprite: b.sprite,
        speed: p.speed,
        level: p.level,
        rival: {
          homeX: at.x,
          homeY: at.y,
          aggroT: 0,
          skillT: 2,
          skills: getArt(b.art)
            .skills.filter((id) => ['attack', 'break', 'power'].includes(getSkill(id).kind))
            .slice(0, 4),
          helper: true,
          followT: 0,
        },
      });
      Object.assign(e, {
        maxHp: Math.round(p.maxHp * k),
        hp: Math.round(p.maxHp * k),
        maxQi: p.maxQi,
        qi: p.maxQi,
        atk: p.atk * k,
        def: p.def * k,
        magic: p.magic * k,
        magicDef: p.magicDef * k,
        hit: p.hit,
        magicHit: p.magicHit,
        dodge: p.dodge,
        crit: p.crit,
        magicCrit: p.magicCrit,
        toughness: p.toughness,
        maxStamina: p.maxStamina,
        stamina: p.maxStamina,
      });
      run.helpers.push(e.id);
    }
    this.h.emit({ type: 'dungeonHelpers', ids: [...run.helpers] });
  }

  /** The player fell: the bosses heal and calm, their adds and decoys go; cleared rooms stay cleared. */
  private wipe(): void {
    const run = this.run!;
    const w = this.w;
    for (const [, t] of run.teles) this.h.emit({ type: 'dungeonTeleEnd', id: t.id, cancelled: true });
    run.teles.clear();
    if (run.surf) this.h.emit({ type: 'dungeonTeleEnd', id: run.surf.id, cancelled: true });
    run.surf = null;
    run.surfT = DUNGEON.surfBeat;
    run.dashes.length = 0;
    run.shadows.length = 0;
    run.spins.clear();
    this.clearDecoys();
    for (const id of run.adds) w.entities.delete(id);
    run.adds = [];
    for (const room of run.rooms) {
      if (room.cleared) continue;
      room.engaged = false;
      for (const id of room.ids) {
        const e = w.entities.get(id);
        if (!e || e.dead) continue;
        Object.assign(e, { hp: e.maxHp, statuses: [], cast: null, stunT: 0, blocking: false });
        if (e.brain && e.brain.state === 'chase') e.brain.state = 'return';
      }
    }
    if (!run.rooms[2]!.cleared) run.decoysDone = false;
    if (!run.rooms[3]!.cleared) run.enraged = false;
    this.h.emit({ type: 'dungeonWipe' });
    this.h.log('sys', 'ปาร์ตี้ล้มทั้งหมด พวกโจรตั้งหลักใหม่ ห้องที่ปราบแล้วยังคงเปิดอยู่');
  }

  /** The boss's chest: once per arena day per character, more for every villager freed. */
  private chest(): void {
    const run = this.run!;
    if (run.bossDown) return;
    run.bossDown = true;
    run.rooms[3]!.cleared = true;
    for (const id of run.adds) {
      const e = this.w.entities.get(id);
      if (e && !e.dead) this.w.entities.delete(id);
    }
    const pr = this.w.progress;
    const today = pr.arena.day;
    const save = (pr.dungeon ??= newDungeonSave());
    if (save.day !== today) Object.assign(save, { day: today, claimed: 0 });
    save.clears++;
    if (save.claimed >= DUNGEON.bossClaimsPerDay) {
      this.h.emit({ type: 'dungeonClear', rewarded: false, items: [], gold: 0, cultivation: 0 });
      this.h.notice('ปราบซาเทียนได้ แต่หีบสมบัติวันนี้เปิดไปแล้ว (รับได้วันละครั้ง)');
      this.h.emit({ type: 'progressChanged' });
      return;
    }
    save.claimed++;
    const r = () => this.h.random();
    const extra = BOSS_CHEST.perVillager;
    const items: string[] = [DUNGEON_SET[Math.floor(r() * DUNGEON_SET.length)]!];
    if (r() < BOSS_CHEST.pearl.chance + extra.chance * run.freed) items.push(BOSS_CHEST.pearl.item);
    if (r() < BOSS_CHEST.manual.chance + extra.chance * run.freed) items.push(BOSS_CHEST.manual.item);
    const gold = BOSS_CHEST.gold + extra.gold * run.freed;
    const cultivation = BOSS_CHEST.cultivation + extra.cultivation * run.freed;
    pr.gold += gold;
    pr.cultivation += cultivation;
    const got: string[] = [];
    for (const id of items) {
      if (!ITEMS[id]) continue;
      if (pr.inventory.length >= BAG_SIZE) {
        this.h.notice('กระเป๋าเต็ม ของบางชิ้นจากหีบหายไปกับน้ำ');
        break;
      }
      pr.inventory.push(id);
      got.push(id);
      this.h.emit({ type: 'loot', id: run.rooms[3]!.ids[0] ?? -1, item: id });
    }
    this.h.emit({ type: 'dungeonClear', rewarded: true, items: got, gold, cultivation });
    this.h.emit({ type: 'progressChanged' });
    this.h.log(
      'sys',
      `เปิดหีบสมบัติเรือจม: ${got.map((id) => `「${ITEMS[id]!.name}」`).join(' ')} ${gold} ตำลึง แต้มฝึกปรือ ${cultivation}`,
    );
    this.h.notice('ทางน้ำลับกลางห้องสมบัติเปิดแล้ว เดินเข้าไปเพื่อกลับท่าเรือ');
  }
}

/** The dungeon system of a World (tests, the client's HUD). */
export const dungeonOf = (w: object): DungeonSystem | null => SYSTEMS.get(w) ?? null;
